import { getAccessToken } from '../shared/auth/token'
import { api } from '../shared/api/axios'
const API_URL = (import.meta as any)?.env?.VITE_API_URL || (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_API_URL) || ''
const GRAPHQL_URL = API_URL ? `${API_URL}/graphql` : '/graphql'

export interface GraphQLResponse<T = any> {
  data?: T
  errors?: Array<{ message: string; extensions?: any }>
}

export async function graphqlRequest<T = any>(
  query: string,
  variables: Record<string, any> = {},
  token?: string | null
): Promise<T> {
  let authToken = token !== undefined ? token : null
  if (authToken === null && typeof window !== 'undefined') {
    authToken = getAccessToken()
    if (!authToken && typeof document !== 'undefined') {
      const match = document.cookie.match(/(?:^|;\s*)(?:goodday_token|token)=([^;]+)/)
      if (match) {
        authToken = match[1]
      }
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`
  }

  const response = await fetch(GRAPHQL_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, variables }),
  })

  if (!response.ok) {
    throw new Error(`Network response was not ok: ${response.status} ${response.statusText}`)
  }

  const result: GraphQLResponse<T> = await response.json()

  if (result.errors && result.errors.length > 0) {
    throw new Error(result.errors[0].message)
  }

  return result.data as T
}

// GRAPHQL QUERIES & MUTATIONS

export const LOGIN_MUTATION = `
  mutation Login($input: LoginDto!) {
    login(input: $input) {
      accessToken
      user {
        id
        email
        name
      }
      organization {
        id
        name
        slug
      }
    }
  }
`

export const REGISTER_MUTATION = `
  mutation Register($input: RegisterDto!) {
    register(input: $input) {
      accessToken
      user {
        id
        email
        name
      }
      organization {
        id
        name
        slug
      }
    }
  }
`

export const ME_QUERY = `
  query Me {
    me {
      accessToken
      user {
        id
        email
        name
      }
      organization {
        id
        name
        slug
      }
    }
  }
`

export const PROJECTS_QUERY = `
  query Projects($organizationId: String!) {
    projects(organizationId: $organizationId) {
      id
      name
      description
      key
      color
      icon
      type
      projectType
      departmentCode
      departments
      issueKeyCounter
      enabledIssueTypes
      status
      tasksCount
      createdAt
      membersCount
      isDealWon
      sourceDealId
      services
      issueStats {
        total
        done
        inProgress
        todo
        progressPercent
      }
      members {
        id
        memberId
        accessLevel
        userId
        name
        email
        avatarUrl
        role
        user {
          id
          name
          email
          avatarUrl
        }
      }
    }
  }
`

export const PROJECT_QUERY = `
  query Project($id: String!) {
    project(id: $id) {
      id
      name
      description
      key
      color
      icon
      type
      status
      departmentCode
      departments
      projectType
      createdAt
      updatedAt
      services
    }
  }
`

export const CREATE_PROJECT_MUTATION = `
  mutation CreateProject($input: CreateProjectDto!) {
    createProject(input: $input) {
      id
      name
      description
      key
      color
      icon
      type
      projectType
      departmentCode
      departments
      status
      tasksCount
      sourceDealId
      isDealWon
      services
    }
  }
`

export const UPDATE_PROJECT_MUTATION = `
  mutation UpdateProject($id: String!, $input: UpdateProjectDto!) {
    updateProject(id: $id, input: $input) {
      id
      name
      description
      color
      icon
      projectType
      departmentCode
      departments
      status
    }
  }
`

export const DELETE_PROJECT_MUTATION = `
  mutation DeleteProject($id: String!) {
    deleteProject(id: $id)
  }
`

export const TASKS_QUERY = `
  query Tasks($organizationId: String!) {
    tasksByOrganization(organizationId: $organizationId) {
      id
      title
      description
      number
      projectId
      statusId
      priorityId
      reporterId
      assigneeIds
      dueDate
      estimatedHours
      loggedHours
      progress
      createdAt
      status {
        id
        name
        color
        category
        isCompleted
      }
      priority {
        id
        name
        color
        label
      }
      project {
        id
        name
        key
        color
      }
    }
  }
`

export const TASKS_BY_PROJECT_QUERY = `
  query TasksByProject($projectId: String!, $mine: Boolean) {
    tasksByProject(projectId: $projectId, mine: $mine) {
      id
      title
      description
      number
      projectId
      typeId
      statusId
      priorityId
      assigneeIds
      startDate
      dueDate
      estimatedHours
      progress
      createdAt
      updatedAt
      status {
        id
        name
        color
        category
        isCompleted
      }
      priority {
        id
        name
        color
        label
      }
      key
      timeSpent
      timeRemaining
      sprintId
      position
      departmentId
      attachments {
        id
        name
        url
        size
        type
        uploadedAt
      }
    }
  }
`

export const TASK_STATUSES_QUERY = `
  query TaskStatuses($organizationId: String!) {
    taskStatuses(organizationId: $organizationId) {
      id
      name
      color
      category
      position
      isCompleted
    }
  }
`

export const TASK_PRIORITIES_QUERY = `
  query TaskPriorities($organizationId: String!) {
    taskPriorities(organizationId: $organizationId) {
      id
      name
      label
      color
      position
    }
  }
`

export const CREATE_TASK_MUTATION = `
  mutation CreateTask($input: CreateTaskDto!) {
    createTask(input: $input) {
      id
      title
      description
      number
      projectId
      statusId
      priorityId
      dueDate
      estimatedHours
      loggedHours
      progress
      status {
        id
        name
        color
      }
      project {
        id
        name
        key
        color
      }
    }
  }
`

export const UPDATE_TASK_MUTATION = `
  mutation UpdateTask($id: String!, $input: UpdateTaskDto!) {
    updateTask(id: $id, input: $input) {
      id
      title
      description
      statusId
      priorityId
      dueDate
      estimatedHours
      progress
      status {
        id
        name
        color
      }
      project {
        id
        name
        key
        color
      }
    }
  }
`

export const UPDATE_TASK_STATUS_MUTATION = `
  mutation UpdateTaskStatus($id: String!, $statusId: String!) {
    updateTaskStatus(id: $id, statusId: $statusId) {
      id
      statusId
      status {
        id
        name
        color
        category
      }
    }
  }
`

export const DELETE_TASK_MUTATION = `
  mutation DeleteTask($id: String!) {
    deleteTask(id: $id)
  }
`

export const COMMENTS_QUERY = `
  query Comments($entityType: String!, $entityId: String!) {
    comments(entityType: $entityType, entityId: $entityId) {
      id
      content
      createdAt
      userId
      user {
        id
        name
        email
      }
    }
  }
`

export const CREATE_COMMENT_MUTATION = `
  mutation CreateComment($input: CreateCommentDto!) {
    createComment(input: $input) {
      id
      content
      createdAt
      user {
        id
        name
        email
      }
    }
  }
`

export const GOALS_QUERY = `
  query Goals($organizationId: String!) {
    goals(organizationId: $organizationId) {
      id
      title
      description
      status
      progress
      startDate
      endDate
      createdAt
    }
  }
`

export const CREATE_GOAL_MUTATION = `
  mutation CreateGoal($input: CreateGoalDto!) {
    createGoal(input: $input) {
      id
      title
      description
      status
      progress
      startDate
      endDate
    }
  }
`

export const UPDATE_GOAL_PROGRESS_MUTATION = `
  mutation UpdateGoalProgress($id: String!, $progress: Float!) {
    updateGoalProgress(id: $id, progress: $progress) {
      id
      progress
      status
    }
  }
`

// WATERFALL / GANTT QUERIES & MUTATIONS

export const WATERFALL_PROJECT_QUERY = `
  query WaterfallProject($projectId: String!) {
    waterfallProject(projectId: $projectId) {
      id
      name
      description
      key
      color
      icon
      type
      status
      startDate
      endDate
      dueDate
      createdAt
      updatedAt
      tasks {
        id
        title
        description
        number
        projectId
        parentId
        typeId
        statusId
        priorityId
        reporterId
        assigneeIds
        startDate
        dueDate
        completedAt
        estimatedHours
        loggedHours
        progress
        createdAt
        updatedAt
        status {
          id
          name
          color
          category
          isCompleted
        }
        priority {
          id
          name
          label
          color
          position
        }
        dependencies {
          id
          sourceTaskId
          targetTaskId
          type
          lagDays
          createdAt
        }
        dependents {
          id
          sourceTaskId
          targetTaskId
          type
          lagDays
          createdAt
        }
      }
      milestones {
        id
        title
        description
        goalId
        projectId
        date
        isCompleted
        completedAt
        color
        createdAt
        updatedAt
      }
    }
  }
`

export const CREATE_TASK_DEPENDENCY_MUTATION = `
  mutation CreateTaskDependency($sourceTaskId: String!, $targetTaskId: String!, $type: String!, $lagDays: Float) {
    createTaskDependency(sourceTaskId: $sourceTaskId, targetTaskId: $targetTaskId, type: $type, lagDays: $lagDays)
  }
`

export const DELETE_TASK_DEPENDENCY_MUTATION = `
  mutation DeleteTaskDependency($id: String!) {
    deleteTaskDependency(id: $id)
  }
`

export const CREATE_MILESTONE_MUTATION = `
  mutation CreateMilestone($title: String!, $date: DateTime!, $projectId: String!, $goalId: String, $description: String, $color: String) {
    createMilestone(title: $title, date: $date, projectId: $projectId, goalId: $goalId, description: $description, color: $color) {
      id
      title
      description
      goalId
      projectId
      date
      isCompleted
      completedAt
      color
      createdAt
      updatedAt
    }
  }
`

// RESOURCE PLANNING QUERIES

export const RESOURCE_WORKLOAD_QUERY = `
  query ResourceWorkload($input: ResourceWorkloadInput!) {
    resourceWorkload(input: $input) {
      workload {
        userId
        userName
        userEmail
        date
        capacity
        allocatedHours
        utilizationPercentage
        utilizationStatus
        tasks {
          id
          number
          title
          projectId
          projectName
          projectKey
          statusName
          statusColor
          estimatedHours
          allocatedHours
          startDate
          dueDate
        }
      }
      dates
      totalMembers
      totalCapacity
      totalAllocated
      overallocatedMembers
    }
  }
`

// PORTFOLIO QUERIES & MUTATIONS

export const PORTFOLIOS_QUERY = `
  query Portfolios($input: PortfolioQueryInput!) {
    portfolios(input: $input) {
      id
      name
      description
      organizationId
      ownerId
      color
      createdAt
      updatedAt
      projects {
        id
        portfolioId
        projectId
        addedAt
        project {
          id
          name
          description
          key
          color
          icon
          type
          status
          organizationId
          ownerId
          startDate
          endDate
          dueDate
          createdAt
          updatedAt
          tasksCount
        }
      }
    }
  }
`

export const PORTFOLIO_QUERY = `
  query Portfolio($id: String!) {
    portfolio(id: $id) {
      portfolio {
        id
        name
        description
        organizationId
        ownerId
        color
        createdAt
        updatedAt
        projects {
          id
          portfolioId
          projectId
          addedAt
          project {
            id
            name
            description
            key
            color
            icon
            type
            status
            organizationId
            ownerId
            startDate
            endDate
            dueDate
            createdAt
            updatedAt
            tasksCount
          }
        }
      }
      metrics {
        totalBudget
        averageCompletionRate
        overdueTasksCount
        totalProjects
        totalTasks
        completedTasks
      }
    }
  }
`

export const CREATE_PORTFOLIO_MUTATION = `
  mutation CreatePortfolio($input: CreatePortfolioDto!) {
    createPortfolio(input: $input) {
      id
      name
      description
      organizationId
      ownerId
      color
      createdAt
      updatedAt
    }
  }
`

export const ADD_PROJECT_TO_PORTFOLIO_MUTATION = `
  mutation AddProjectToPortfolio($input: AddProjectToPortfolioDto!) {
    addProjectToPortfolio(input: $input) {
      id
      portfolioId
      projectId
      addedAt
      project {
        id
        name
        description
        key
        color
        icon
        type
        status
        organizationId
        ownerId
        startDate
        endDate
        dueDate
        createdAt
        updatedAt
        tasksCount
      }
    }
  }
`

export const REMOVE_PROJECT_FROM_PORTFOLIO_MUTATION = `
  mutation RemoveProjectFromPortfolio($portfolioId: String!, $projectId: String!) {
    removeProjectFromPortfolio(portfolioId: $portfolioId, projectId: $projectId)
  }
`

// PORTFOLIO TIMELINE (migrated from JIRA-ZYNX)

export const PORTFOLIO_TIMELINE_QUERY = `
  query PortfolioTimeline($input: PortfolioTimelineQueryInput!) {
    portfolioTimeline(input: $input) {
      timeline {
        epics {
          epic {
            id
            key
            title
            type
            priority
            assigneeId
            parentId
            statusCategory
            progress
            startDate
            dueDate
            projectId
            projectKey
            projectColor
          }
          effectiveStartDate
          effectiveDueDate
          startIsRollup
          dueIsRollup
          children {
            id
            key
            title
            type
            priority
            assigneeId
            parentId
            statusCategory
            progress
            startDate
            dueDate
            projectId
            projectKey
            projectColor
          }
        }
        unassigned {
          id
          key
          title
          type
          priority
          assigneeId
          parentId
          statusCategory
          progress
          startDate
          dueDate
          projectId
          projectKey
          projectColor
        }
        dependencies {
          blockerId
          blockedId
        }
      }
      projectIds
      accessibleProjectIds
    }
  }
`

// PORTFOLIO TIMELINE API

export interface PortfolioTimelineTask {
  id: string
  key: string
  title: string
  type: string
  priority: string | null
  assigneeId: string | null
  parentId: string | null
  statusCategory: string | null
  progress: number | null
  startDate: string | null
  dueDate: string | null
  projectId: string
  projectKey: string
  projectColor: string
}

export interface PortfolioEpicTimelineGroup {
  epic: PortfolioTimelineTask
  effectiveStartDate: string | null
  effectiveDueDate: string | null
  startIsRollup: boolean
  dueIsRollup: boolean
  children: PortfolioTimelineTask[]
}

export interface PortfolioTimelineDependency {
  blockerId: string
  blockedId: string
}

export interface PortfolioTimelineResult {
  epics: PortfolioEpicTimelineGroup[]
  unassigned: PortfolioTimelineTask[]
  dependencies: PortfolioTimelineDependency[]
}

export interface PortfolioTimelineResponse {
  timeline: PortfolioTimelineResult
  projectIds: string[]
  accessibleProjectIds: string[]
}

export async function getPortfolioTimeline(
  organizationId: string,
  projectIds?: string[]
): Promise<PortfolioTimelineResponse> {
  const data = await graphqlRequest<{ portfolioTimeline: PortfolioTimelineResponse }>(
    PORTFOLIO_TIMELINE_QUERY,
    { input: { organizationId, projectIds } }
  )
  return data.portfolioTimeline
}

// SALES DOCS MODULE TYPES & API

export interface SalesDocCategory {
  id: string
  name: string
  description?: string | null
  category: string
  subCategory?: string | null
  parentId?: string | null
  order: number
  documentsCount?: number
}

export interface SalesDocument {
  id: string
  title: string
  description?: string | null
  content?: string | null
  type: string
  fileId?: string | null
  categoryId: string
  categoryName?: string
  organizationId: string
  authorId: string
  authorName?: string
  status: string
  tags: string[]
  viewCount: number
  createdAt: string
  updatedAt: string
  publishedAt?: string | null
  versions?: Array<{
    id: string
    version: number
    title: string
    content: string
    changelog?: string | null
    createdAt: string
  }>
}

export const SALES_DOC_CATEGORIES_QUERY = `
  query SalesDocCategories($organizationId: String) {
    salesDocCategories(organizationId: $organizationId) {
      id
      name
      description
      category
      subCategory
      parentId
      order
      documentsCount
    }
  }
`

export const SALES_DOCUMENTS_QUERY = `
  query SalesDocuments(
    $organizationId: String
    $categoryId: String
    $category: SalesDocCategory
    $subCategory: SalesDocSubCategory
    $search: String
    $limit: Int
  ) {
    salesDocuments(
      organizationId: $organizationId
      categoryId: $categoryId
      category: $category
      subCategory: $subCategory
      search: $search
      limit: $limit
    ) {
      id
      title
      description
      content
      type
      fileId
      categoryId
      categoryName
      authorName
      status
      tags
      viewCount
      createdAt
      updatedAt
    }
  }
`

export const SALES_DOCUMENT_DETAIL_QUERY = `
  query SalesDocument($id: ID!, $organizationId: String) {
    salesDocument(id: $id, organizationId: $organizationId) {
      id
      title
      description
      content
      type
      fileId
      categoryId
      categoryName
      authorName
      status
      tags
      viewCount
      createdAt
      updatedAt
      publishedAt
    }
  }
`

export const CREATE_SALES_DOCUMENT_MUTATION = `
  mutation CreateSalesDocument($input: CreateSalesDocumentInput!, $organizationId: String) {
    createSalesDocument(input: $input, organizationId: $organizationId) {
      id
      title
      description
      content
      type
      categoryId
      categoryName
      authorName
      status
      tags
      createdAt
    }
  }
`

export async function getSalesDocCategories(organizationId?: string): Promise<SalesDocCategory[]> {
  try {
    const data = await graphqlRequest<{ salesDocCategories: SalesDocCategory[] }>(
      SALES_DOC_CATEGORIES_QUERY,
      { organizationId }
    )
    return data.salesDocCategories || []
  } catch {
    const res = await api.get<SalesDocCategory[]>(`/sales-docs/categories${organizationId ? `?organizationId=${organizationId}` : ''}`)
    return res.data
  }
}

export async function getSalesDocuments(params: {
  organizationId?: string
  categoryId?: string
  category?: string
  subCategory?: string
  search?: string
  limit?: number
} = {}): Promise<SalesDocument[]> {
  try {
    const data = await graphqlRequest<{ salesDocuments: SalesDocument[] }>(
      SALES_DOCUMENTS_QUERY,
      params
    )
    return data.salesDocuments || []
  } catch {
    const query = new URLSearchParams()
    if (params.organizationId) query.set('organizationId', params.organizationId)
    if (params.categoryId) query.set('categoryId', params.categoryId)
    if (params.category) query.set('category', params.category)
    if (params.subCategory) query.set('subCategory', params.subCategory)
    if (params.search) query.set('search', params.search)
    if (params.limit) query.set('limit', String(params.limit))
    const res = await api.get<SalesDocument[]>(`/sales-docs/documents?${query.toString()}`)
    return res.data
  }
}

export async function getSalesDocumentById(id: string, organizationId?: string): Promise<SalesDocument | null> {
  try {
    const data = await graphqlRequest<{ salesDocument: SalesDocument | null }>(
      SALES_DOCUMENT_DETAIL_QUERY,
      { id, organizationId }
    )
    return data.salesDocument
  } catch {
    const res = await api.get<SalesDocument | null>(`/sales-docs/documents/${id}${organizationId ? `?organizationId=${organizationId}` : ''}`)
    if (!res.data) return null
    return res.data
  }
}

export async function createSalesDocument(input: {
  title: string
  description?: string
  content?: string
  type?: string
  categoryId?: string
  category?: string
  subCategory?: string
  status?: string
  tags?: string[]
}, organizationId?: string): Promise<SalesDocument> {
  try {
    const data = await graphqlRequest<{ createSalesDocument: SalesDocument }>(
      CREATE_SALES_DOCUMENT_MUTATION,
      { input, organizationId }
    )
    return data.createSalesDocument
  } catch {
    const res = await api.post<SalesDocument>(`/sales-docs/documents${organizationId ? `?organizationId=${organizationId}` : ''}`, input)
    return res.data
  }
}

// DEALS API

export interface Deal {
  id: string
  title: string
  client: string
  stage: string
  value: number
  probability: number
  owner?: string | null
  closeDate?: string | null
  notes?: string | null
  services?: string[]
  organizationId: string
  contactPerson?: string
  contactDesignation?: string
  contactEmail?: string
  contactPhone?: string
  /** The quotation raised for this deal, if any (set by the API). */
  quotation?: { id: string; number: string; status: string } | null
  /** The deal is won, or its advance payment is in, so a delivery project may be launched (set by the API). */
  canLaunchProject?: boolean
  address?: string
  billingAddress?: string
  clientAddress?: string
  gstin?: string
  clientGstin?: string
  /** The customer's state ("Karnataka (29)"); the place of supply when they have no GSTIN. */
  clientState?: string
  source?: string
  createdAt: string
  updatedAt: string
}

export const DEALS_QUERY = `
  query Deals($organizationId: String, $stage: String) {
    deals(organizationId: $organizationId, stage: $stage) {
      id
      title
      client
      stage
      value
      probability
      owner
      closeDate
      notes
      services
      organizationId
      createdAt
      updatedAt
    }
  }
`

export const CREATE_DEAL_MUTATION = `
  mutation CreateDeal($input: CreateDealDto!, $organizationId: String) {
    createDeal(input: $input, organizationId: $organizationId) {
      id
      title
      client
      stage
      value
      probability
      owner
      closeDate
      notes
      organizationId
      createdAt
      updatedAt
    }
  }
`

export async function getDeals(params: { organizationId?: string; stage?: string } = {}): Promise<Deal[]> {
  try {
    const query = new URLSearchParams()
    if (params.organizationId) query.set('organizationId', params.organizationId)
    if (params.stage) query.set('stage', params.stage)
    const res = await api.get<any>(`/deals?${query.toString()}`)
    const data = res.data
    return Array.isArray(data) ? data : (data?.deals || data?.data || [])
  } catch {}

  try {
    const data = await graphqlRequest<{ deals: Deal[] }>(DEALS_QUERY, params)
    if (data?.deals) return data.deals
  } catch {}

  return []
}

export async function createDeal(input: {
  title: string
  client: string
  stage?: string
  value?: number
  probability?: number
  owner?: string
  closeDate?: string
  notes?: string
}, organizationId?: string): Promise<Deal> {
  // First try the goodday REST API endpoint
  try {
    const res = await api.post<Deal>('/deals', { ...input, organizationId })
    return res.data
  } catch {}

  try {
    const data = await graphqlRequest<{ createDeal: Deal }>(CREATE_DEAL_MUTATION, { input, organizationId })
    if (data?.createDeal) return data.createDeal
  } catch {}

  throw new Error('Failed to create deal')
}

export interface ProjectMember {
  id: string
  projectId: string
  memberId: string
  accessLevel: string
  userId?: string | null
  name?: string | null
  email?: string | null
  avatarUrl?: string | null
  role?: string | null
}

export const PROJECT_MEMBERS_QUERY = `
  query ProjectMembers($projectId: String!) {
    projectMembers(projectId: $projectId) {
      id
      projectId
      memberId
      accessLevel
      userId
      name
      email
      avatarUrl
      role
    }
  }
`

export const ADD_PROJECT_MEMBER_MUTATION = `
  mutation AddProjectMember($input: AddProjectMemberInput!) {
    addProjectMember(input: $input) {
      id
      projectId
      memberId
      accessLevel
      userId
      name
      email
      avatarUrl
      role
    }
  }
`

export const UPDATE_PROJECT_MEMBER_MUTATION = `
  mutation UpdateProjectMember($input: UpdateProjectMemberInput!) {
    updateProjectMember(input: $input) {
      id
      projectId
      memberId
      accessLevel
      userId
      name
      email
      avatarUrl
      role
    }
  }
`

export const REMOVE_PROJECT_MEMBER_MUTATION = `
  mutation RemoveProjectMember($projectId: String!, $userId: String!) {
    removeProjectMember(projectId: $projectId, userId: $userId)
  }
`


